import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { colors } from '@guryou/colors';
import { imageNotFound } from '@guryou/html-components';
import { PROVIDER_GET_GENERAL_INFO, PROVIDER_LATEST_WIDGET, resetState } from '@guryou/redux';
import { printPrice } from '@guryou/utils/build/stringUtils';
import { readWidgetAuthBrandingFromSession } from 'constants/widgetIntegrationBranding';
import { Icon } from '../../components/shared';

function darkenHex(hex, amount = 0.12) {
  const raw = String(hex || '')
    .trim()
    .replace(/^#/, '');
  const normalized =
    raw.length === 3
      ? raw
          .split('')
          .map(ch => ch + ch)
          .join('')
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }
  const clamp = v => Math.max(0, Math.min(255, Math.round(v)));
  const r = clamp(parseInt(normalized.slice(0, 2), 16) * (1 - amount));
  const g = clamp(parseInt(normalized.slice(2, 4), 16) * (1 - amount));
  const b = clamp(parseInt(normalized.slice(4, 6), 16) * (1 - amount));
  const toHex = v => v.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

const VideoCourseCard = ({ course, isEnrolled = false }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const [imgSrc, setImgSrc] = useState(course.imageUrl);
  const widgetAuthBranding = React.useMemo(() => readWidgetAuthBrandingFromSession(), []);
  const authAccent = widgetAuthBranding?.accentColor || null;
  const buttonBg = authAccent || 'var(--feel-primary)';
  const buttonHoverBg = authAccent ? darkenHex(authAccent, 0.12) || authAccent : 'var(--feel-primary-dark)';

  const handleImageError = () => {
    setImgSrc(imageNotFound);
  };

  const handleLearnMore = e => {
    e.stopPropagation(); // Prevent card click
    if (isEnrolled) {
      return history.push(`/my-video-courses/${course.id}`);
    }
    return history.push(`/video-courses/${course.id}`);
  };

  const handleProviderClick = e => {
    e.stopPropagation(); // Prevent card click
    dispatch(resetState(PROVIDER_GET_GENERAL_INFO));
    dispatch(resetState(PROVIDER_LATEST_WIDGET));
    return history.push(`/${i18n.language}/provider/${course.providerId}`);
  };

  const handleCardClick = () => {
    if (isEnrolled) {
      history.push(`/my-video-courses/${course.id}`);
      return;
    }
    history.push(`/video-courses/${course.id}`);
  };

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-xl hover:shadow-feel-primary/20 transition-all duration-300 border border-mercury shadow-sm hover:border-feel-primary/30">
      {/* Image Container */}
      <div className="relative h-48 overflow-hidden cursor-pointer" onClick={handleCardClick}>
        <img
          className="absolute inset-0 h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
          src={imgSrc}
          alt={course.name}
          onError={handleImageError}
          loading="lazy"
        />

        {/* Category Badge */}
        {course?.groupName && (
          <div className="absolute top-3 left-3">
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-white shadow-lg backdrop-blur-sm"
              style={{ backgroundColor: `#${course.groupColor}` }}
            >
              {course.groupName}
            </span>
          </div>
        )}

        {isEnrolled && (
          <div className="absolute bottom-3 left-3">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold text-white shadow-lg bg-green-600">
              {t('VIDEO_COURSE_PAGE.alreadyEnrolled', 'Già iscritto')}
            </span>
          </div>
        )}

        {/* Price Badge */}
        <div className="absolute top-3 right-3">
          <span
            className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold text-white shadow-lg"
            style={{ backgroundColor: 'var(--feel-primary)' }}
          >
            {printPrice(course.price)}
          </span>
        </div>

        {/* Play Button Overlay */}
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 shadow-lg">
            <Icon name="play" size="lg" className="text-feel-primary ml-1" />
          </div>
        </div>
      </div>

      {/* Content Container */}
      <div className="flex flex-col h-full p-6 bg-white">
        {/* Top Content */}
        <div className="flex-1">
          <div onClick={handleCardClick} className="cursor-pointer">
            <h3
              className="text-xl font-bold mb-1 leading-tight line-clamp-1 group-hover:text-feel-primary transition-colors duration-200"
              style={{ color: colors.mineShaft }}
            >
              {course.name}
            </h3>
          </div>
          {course.providerName && (
            <button onClick={handleProviderClick} className="flex items-center mb-4 w-full text-left">
              <Icon name="user" size="sm" className="text-gray-400 mr-2 hover:text-feel-primary transition-colors duration-200" />
              <span className="text-sm text-gray-500 hover:text-feel-primary hover:underline duration-200">{course.providerName}</span>
            </button>
          )}
          {/* Description */}
          <div
            className="text-sm text-gray-600 mb-4 line-clamp-3 leading-relaxed max-h-20 overflow-hidden"
            dangerouslySetInnerHTML={{
              __html: course.description,
            }}
          />

          {/* Provider Info */}
        </div>

        {/* Bottom Section - Price and Button - Always at bottom */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          {/* Price */}
          <div className="text-2xl font-bold" style={{ color: colors.mineShaft }}>
            {printPrice(course.price)}
          </div>

          {/* Action Button */}
          <button
            onClick={handleLearnMore}
            className="text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 flex items-center shadow-lg hover:shadow-xl hover:scale-105"
            style={{ backgroundColor: buttonBg }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = buttonHoverBg;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = buttonBg;
            }}
          >
            <Icon name="play" size="sm" className="mr-2" />
            {isEnrolled ? t('GENERAL.continue', 'Continua') : t('GENERAL.view')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoCourseCard;
